// dependencies
import React, { useEffect, useState } from "react";
// redux
import { useSelector } from "react-redux";

import { faBell, faMessageLines } from "@fortawesome/pro-regular-svg-icons";

// components
import { AdminContainer, AdminTitle } from "../../../components/Admin";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import Tabs from "../../../components/Tabs";
import TeamsFeedbackSettings from "./TeamsFeedbackSettings";
import { TeamsUserNotificationSettings } from "./TeamsUserNotificationSettings";

// store
import useStore from "./store";

import {
	useCompanyFunction,
	useTeamsAutoSyncSettings,
	useTeamsConnectedUserCount,
	useTeamsHasAdminConsent,
	useTeamsLastSyncDate,
} from "../../../api/hooks";
// hooks
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

// selectors
import { getUserCompanyName } from "../../../selectors/app";

// utils
// styles
import css from "./Teams.module.scss";

const TeamsIntegrationSetup = () => {
	const { data: automaticSyncSettings } = useTeamsAutoSyncSettings();
	const { data: connectedUserCount, refetch: refetchConnectedUserCount } = useTeamsConnectedUserCount();
	const { data: lastSyncDate, refetch: refecthLastSyncDate } = useTeamsLastSyncDate();
	const { data: adminConsentResponse, isFetching: fetchingAdminConsent } = useTeamsHasAdminConsent();

	// Logan feature flag for Microsoft Teams Integration
	const { value: teamsIntegrationFeatureFlagEnabled } = useFeatureFlagQuery(
		"ff-is-enabled-microsoft-teams-integration",
		false
	);

	const {
		companyFunction: teamsUserNotificationsFeatureEnabled,
		isLoading: fetchingTeamsUserNotificationsFeatureEnabled,
	} = useCompanyFunction("teams-user-notifications");
	const {
		companyFunction: teamsIntegrationFeatureEnabled,
		isLoading: fetchingTeamsIntegrationFeatureEnabledFeatureEnabled,
	} = useCompanyFunction("teams-integration");

	const { value: teamsUserNotificationsFF } = useFeatureFlagQuery("ff-is-teams-user-notifications-enabled", false);

	// Teams Integration is only available when both the Logan feature flag is enabled AND the company function is enabled
	const isTeamsIntegrationAvailable =
		teamsIntegrationFeatureFlagEnabled && teamsIntegrationFeatureEnabled === "enabled";

	const showNotificationsTab =
		teamsUserNotificationsFF &&
		teamsUserNotificationsFeatureEnabled === "enabled" &&
		!fetchingTeamsUserNotificationsFeatureEnabled;

	const [
		automaticSyncEnabled,
		setAutomaticSyncEnabled,
		numConnectedUsers,
		setNumConnectedUsers,
		adminConsent,
		setAdminConsent,
	] = useStore((state) => [
		state.automaticSyncEnabled,
		state.setAutomaticSyncEnabled,
		state.numConnectedUsers,
		state.setNumConnectedUsers,
		state.adminConsent,
		state.setAdminConsent,
	]);

	const [activeTab, setActiveTab] = useState("feedback");

	const refetchTeamsIntegrationData = () => {
		refetchConnectedUserCount();
		refecthLastSyncDate();
	};

	useEffect(() => {
		setAutomaticSyncEnabled(automaticSyncSettings?.proactive_install === "auto");
	}, [automaticSyncSettings]);

	useEffect(() => {
		setNumConnectedUsers(connectedUserCount?.count);
	}, [connectedUserCount]);

	useEffect(() => {
		if (!adminConsentResponse) return;
		setAdminConsent(adminConsentResponse);
	}, [adminConsentResponse]);

	// select data from state
	const companyName = useSelector((state) => getUserCompanyName(state));

	return (
		<AdminContainer>
			<div className={css.teamsContainer}>
				<AdminTitle>Microsoft Teams Setup</AdminTitle>
				<PageTitle title={`MicrosoftTeams - ${companyName} - PerformYard`} />
				<Tabs
					className={css.tabs}
					onSelectTitle={(id) => {
						setActiveTab(id);
					}}
					activeTitle={activeTab}
					titles={[
						{
							id: "feedback",
							label: "Feedback",
							icon: faMessageLines,
						},
						// include only if feature is enabled
						...(showNotificationsTab
							? [
									{
										id: "user-notifications",
										label: "Notifications",
										icon: faBell,
									},
								]
							: []),
					]}
				/>

				<Spacer height={4} />

				{/* TODO: add oAuth Button here using has admin consent */}

				{activeTab == "feedback" ? (
					<TeamsFeedbackSettings
						adminConsent={adminConsent}
						teamsIntegrationFeatureEnabled={isTeamsIntegrationAvailable}
						showLoadingState={fetchingAdminConsent || fetchingTeamsIntegrationFeatureEnabledFeatureEnabled}
					/>
				) : null}
				{activeTab == "user-notifications" ? (
					<TeamsUserNotificationSettings
						automaticSyncEnabled={automaticSyncEnabled}
						numConnectedUsers={numConnectedUsers}
						lastSyncDate={lastSyncDate}
						adminConsent={adminConsent}
						refetchTeamsIntegrationData={refetchTeamsIntegrationData}
					/>
				) : null}
			</div>
		</AdminContainer>
	);
};

export default TeamsIntegrationSetup;
